/** Thrown for every non-2xx response and for a response that fails contract validation. */
export class ContractError extends Error {
    operationId;
    status;
    body;
    kind;
    name = 'ContractError';
    constructor(operationId, status, body, kind) {
        super(describe(operationId, status, body, kind));
        this.operationId = operationId;
        this.status = status;
        this.body = body;
        this.kind = kind;
    }
    get code() {
        return errorBody(this.body)?.code;
    }
    get requestId() {
        return errorBody(this.body)?.requestId;
    }
}
function errorBody(body) {
    if (typeof body !== 'object' || body === null)
        return undefined;
    return body;
}
function describe(operationId, status, body, kind) {
    if (kind === 'contract')
        return `${operationId}: response ${status} does not match the contract`;
    const e = errorBody(body);
    const code = e?.code ? ` ${e.code}` : '';
    const message = e?.message ? `: ${e.message}` : '';
    return `${operationId}: HTTP ${status}${code}${message}`;
}
export function buildUrl(baseUrl, pathTemplate, params, query) {
    const path = pathTemplate.replace(/\{([A-Za-z][A-Za-z0-9]*)\}/g, (_m, name) => {
        const value = params[name];
        if (value === undefined || value === null)
            throw new Error(`missing path parameter "${name}" for ${pathTemplate}`);
        return encodeURIComponent(scalar(value, name));
    });
    // The shells call same-origin with a relative base (`/api/platform`); `new URL()` refuses a
    // relative URL without a base, so resolve against a placeholder and hand back path + query.
    const joined = `${baseUrl.replace(/\/+$/, '')}${path}`;
    const absolute = /^[a-z][a-z0-9+.-]*:/i.test(joined);
    const url = absolute ? new URL(joined) : new URL(joined, RELATIVE_BASE);
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null)
            continue;
        if (Array.isArray(value))
            value.forEach((v) => url.searchParams.append(key, scalar(v, key)));
        else
            url.searchParams.set(key, scalar(value, key));
    }
    return absolute ? url.toString() : `${url.pathname}${url.search}`;
}
const RELATIVE_BASE = 'http://relative.invalid';
function scalar(value, name) {
    switch (typeof value) {
        case 'string':
            return value;
        case 'number':
        case 'boolean':
        case 'bigint':
            return String(value);
        default:
            throw new Error(`parameter "${name}" must be a string, number or boolean`);
    }
}
export function createCaller(options) {
    const doFetch = options.fetch ?? globalThis.fetch;
    const validate = options.validateResponses ?? true;
    return async (op, input = {}) => {
        const params = op.params.parse(input.params ?? {});
        const query = op.query.parse(input.query ?? {});
        const body = op.body ? op.body.parse(input.body) : undefined;
        const headers = {
            accept: 'application/json',
            ...(await options.headers?.()),
        };
        if (op.body)
            headers['content-type'] = 'application/json';
        const response = await doFetch(buildUrl(options.baseUrl, op.path, params, query), {
            method: op.method,
            headers,
            ...(op.body ? { body: JSON.stringify(body) } : {}),
            ...(input.signal ? { signal: input.signal } : {}),
        });
        const text = await response.text();
        const parsed = text.length === 0 ? undefined : safeJson(text);
        if (response.status < 200 || response.status >= 300) {
            throw new ContractError(op.operationId, response.status, parsed, 'http');
        }
        const schema = op.responses[String(response.status)];
        if (!schema)
            throw new ContractError(op.operationId, response.status, parsed, 'contract');
        if (!validate)
            return parsed;
        const result = schema.safeParse(parsed);
        if (!result.success)
            throw new ContractError(op.operationId, response.status, parsed, 'contract');
        return result.data;
    };
}
function safeJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
//# sourceMappingURL=runtime.js.map